import Text from "@ingka/text";
import { useLocation, useNavigate, useParams } from "react-router";
import { useAuth } from "@/hooks/auth";
import {
  useCurrentUploadId,
  useCurrentUploadQuery,
  useUploadValidationQuery,
} from "@/hooks/uploads";
import { Loading } from "../Loading/Loading";
import { useMetadataQuery } from "@/hooks/metadata";
import { NavigationButtons } from "../NavigationButtons/NavigationButtons";
import ReleaseDate from "./components/mandatory/ReleaseDate";
import { Copyright } from "./components/mandatory/Copyright";
import { ProducedBy } from "./components/mandatory/ProducedBy";
import Subdelivery from "./components/mandatory/SubDelivery/Subdelivery";
import NegotiatedUsageRights from "./components/mandatory/NegotiatedUsageRights/NegotiatedUsageRights";
import ProductionOrderedBy from "./components/mandatory/ProductionOrderedBy/ProductionOrderedBy";
import MarketTeamAgency from "./components/mandatory/MarketTeamAgency/MarketTeamAgency";
import CampaignOrDeliveryName from "./components/mandatory/CampaignOrDeliveryName/CampaignOrDeliveryName";
import EndOfNegotiatedUsageRights from "./components/mandatory/EndOfNegotiatedUsageRights";
import FiscalYear from "./components/mandatory/FiscalYear";
import Launch from "./components/mandatory/Launch";
import Objective from "./components/mandatory/Objective";
import Channels from "./components/mandatory/Channels";
import DeleteUpload from "../DeleteUpload/DeleteUpload";
import { useStore } from "@/services/store";
import { useShallow } from "zustand/react/shallow";
import { useMetadataFields } from "@/components/MetadataFields/hooks";
import { NEGOTIATED_USAGE_RIGHTS_TOOLTIP } from "@/constants";
import {
  FieldAccessContext,
  isLockedForOwner,
  isUploadFinalized,
} from "./formFieldsAccessRules";
import { toRouteError } from "@/errors/routeError";

const MandatoryMetadata = () => {
  const { user } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const uploadId = useCurrentUploadId();
  const metadataQuery = useMetadataQuery();
  const { pathname } = useLocation();
  const currentUploadQuery = useCurrentUploadQuery();
  const { activeAsset } = useMetadataFields();
  const { refetch: validateMetadata } = useUploadValidationQuery(uploadId);
  const { resetPlaceholderErrors, state } = useStore(
    useShallow((state) => ({
      state,
      resetPlaceholderErrors: state.clearPlaceholderErrors,
    })),
  );

  const assetIds = params.assetIds;

  if (currentUploadQuery.isError || metadataQuery.isError) {
    throw toRouteError(metadataQuery.error ?? currentUploadQuery.error, {
      scope: "metadata",
      uploadId,
    });
  }

  if (currentUploadQuery.isLoading || !metadataQuery.isSuccess) {
    return <Loading text="Loading asset metadata" />;
  }

  const { isAdmin, isIngkaUser } = user;
  const upload = currentUploadQuery.data!;
  const { state: uploadState, type: uploadType } = upload;
  const fieldAccessContext: FieldAccessContext = {
    isAdmin,
    isIngkaUser,
    uploadState,
    uploadType,
    producedBy: activeAsset.producedBy,
  };

  const isDisabled =
    isLockedForOwner(fieldAccessContext) ||
    isUploadFinalized(fieldAccessContext);

  const { bynder, producedBy, producers, launch, channels } =
    metadataQuery.data;

  return (
    <div className="flex flex-col">
      <section className="flex-1">
        <Text tagName="h3" headingSize="xs" className="mb-space100">
          Mandatory metadata
        </Text>
        <div className="grid grid-cols-2 gap-x-space150 gap-y-space75 pr-space25">
          <div className="col-start-1 row-start-1">
            <ReleaseDate disabled={isDisabled} />
          </div>
          <div className="col-start-2 row-start-1">
            <Copyright
              options={bynder.Copyright}
              fieldAccessContext={fieldAccessContext}
            />
          </div>
          <div className="col-span-2 col-start-1 row-start-2">
            <EndOfNegotiatedUsageRights fieldAccessContext={fieldAccessContext} />
          </div>
          <div className="col-start-1 row-start-3">
            <NegotiatedUsageRights
              options={bynder["Negotiated usage rights"]}
              disabled={isDisabled}
              tooltip={
                NEGOTIATED_USAGE_RIGHTS_TOOLTIP[
                  user.isIngkaUser ? "ingka" : "external"
                ]
              }
            />
          </div>
          {activeAsset.producedBy === "AGENCY" && (
            <div className="col-start-2 row-start-3">
              <ProductionOrderedBy
                options={bynder.Markets}
                disabled={isDisabled}
              />
            </div>
          )}
          <div className="col-start-1 row-start-4">
            <ProducedBy
              options={producedBy}
              fieldAccessContext={fieldAccessContext}
            />
          </div>
          <div className="col-start-2 row-start-4">
            <MarketTeamAgency options={producers} disabled={isDisabled} />
          </div>
          <div className="col-start-1 row-start-5">
            <CampaignOrDeliveryName
              fieldAccessContext={fieldAccessContext}
              options={bynder["Campaigns / Deliveries"]}
            />
          </div>
          <div className="col-start-2 row-start-5">
            <Subdelivery
              fieldAccessContext={fieldAccessContext}
              options={bynder["Sub delivery"]}
            />
          </div>
          <div className="col-start-2 row-span-3 row-start-6">
            <Channels
              channels={channels}
              fieldAccessContext={fieldAccessContext}
            />
          </div>
          <div className="col-start-1 row-start-6">
            <FiscalYear options={bynder["Fiscal year"]} disabled={isDisabled} />
          </div>
          <div className="col-start-1 row-start-7">
            <Launch options={launch} disabled={isDisabled} />
          </div>
          <div className="col-start-1 row-start-8">
            <Objective options={bynder.Objective} disabled={isDisabled} />
          </div>
        </div>
      </section>
      <section className="mb-space150 mt-space300 flex flex-row justify-end">
        {upload.state === "DRAFT" && (
          <DeleteUpload
            upload={upload}
            buttonText="Delete Draft"
            navigate={() => navigate("/overview")}
          />
        )}
        <NavigationButtons
          onPrev={() => {
            if (isAdmin && pathname.includes("/admin")) {
              navigate(`/admin/${uploadId}`);
            } else if (isDisabled) {
              navigate(`/uploads/${uploadId}/comments`);
            } else {
              if (upload.type === "VIDEO") {
                navigate(`/uploads/${uploadId}/mandatory-video-metadata`);
              } else {
                navigate(`/uploads/${uploadId}/confirm-files`);
              }
            }
          }}
          disabledNext={state.validationErrors.length > 0}
          onContinue={async () => {
            const { data: isValid } = await validateMetadata();
            if (isValid) {
              resetPlaceholderErrors();
              if (isAdmin && pathname.includes("/admin")) {
                navigate(
                  `/admin/${uploadId}/edit/optional-metadata/${assetIds}`,
                );
              } else {
                navigate(`/uploads/${uploadId}/optional-metadata`);
              }
            }
          }}
        />
      </section>
    </div>
  );
};

export default MandatoryMetadata;
